import { FlaskConical, Inbox, Cpu, Megaphone, Factory, HardHat, ShoppingBag, HeartPulse, Briefcase, LineChart, Truck, Wheat, UtensilsCrossed, GraduationCap, Building2, Shapes } from 'lucide-react';

const Icons = { Cpu, Megaphone, Factory, HardHat, ShoppingBag, HeartPulse, Briefcase, LineChart, Truck, Wheat, UtensilsCrossed, GraduationCap, Building2, Shapes };
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { hueFrom, initials } from '@/lib/format';

export const DemoBadge = ({ label = 'DEMO', className }) => (
  <span data-testid="demo-badge" className={cn('inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground', className)}>
    <FlaskConical className="h-3 w-3" /> {label}
  </span>
);

export const RecentBadge = ({ className }) => (
  <span data-testid="recent-opportunity-badge" className={cn('inline-flex items-center gap-1.5 rounded-md border border-[rgba(255,59,92,0.3)] bg-[rgba(255,59,92,0.12)] px-1.5 py-0.5 text-[11px] font-medium text-[rgb(255,130,150)]', className)}>
    <span className="h-1.5 w-1.5 rounded-full bg-[#ff3b5c] shadow-[0_0_6px_#ff3b5c]" /> Recente
  </span>
);

export const SponsoredBadge = ({ className }) => (
  <span data-testid="sponsored-badge" className={cn('inline-flex items-center rounded-md border border-amber-300/25 bg-amber-300/10 px-1.5 py-0.5 text-[11px] font-medium text-amber-200', className)}>
    Patrocinado · exemplo
  </span>
);

export const Chip = ({ children, className }) => (
  <span className={cn('inline-flex items-center rounded-md border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[11px] text-foreground/85', className)}>{children}</span>
);

export const CategoryIcon = ({ name, className }) => {
  const Icon = Icons[name] || Icons.Shapes;
  return <Icon className={className} />;
};

export const CompanyAvatar = ({ company, size = 44, className }) => {
  const hue = hueFrom(company?.name);
  const logo = company?.logoUrl;
  return (
    <div
      className={cn('relative grid shrink-0 place-items-center overflow-hidden rounded-xl border border-white/10 font-display font-semibold text-white', className)}
      style={{ width: size, height: size, fontSize: size * 0.34, background: `linear-gradient(140deg, hsl(${hue} 70% 22%), #0b1018 70%)` }}
      aria-hidden="true"
    >
      {logo ? (
        <img src={logo} alt="" className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <>
          <span className="relative z-10">{initials(company?.name)}</span>
          <span className="absolute -right-2 -top-2 h-6 w-6 rotate-45 border border-cyan-300/30" />
        </>
      )}
    </div>
  );
};

/** Capa gerada (sem fotos de terceiros). */
export const GeneratedCover = ({ seed = '', className, children }) => {
  const hue = hueFrom(seed);
  return (
    <div className={cn('relative overflow-hidden bg-[#090d14]', className)}>
      <div className="absolute inset-0 grid-floor opacity-70" />
      <div className="absolute inset-0" style={{ background: `radial-gradient(420px 220px at 80% 10%, hsla(${hue},90%,55%,0.22), transparent 70%), radial-gradient(380px 200px at 10% 100%, rgba(59,130,246,0.14), transparent 70%)` }} />
      <svg className="absolute inset-0 h-full w-full opacity-40" preserveAspectRatio="none" viewBox="0 0 400 120" aria-hidden="true">
        <path d="M0 90 C80 60 120 100 200 70 S320 30 400 50" fill="none" stroke={`hsl(${hue} 90% 65%)`} strokeWidth="0.8" strokeDasharray="4 6" />
        <path d="M0 105 C90 80 150 110 230 85 S340 60 400 75" fill="none" stroke="rgba(59,130,246,0.6)" strokeWidth="0.6" />
      </svg>
      {children}
    </div>
  );
};

export const EmptyState = ({ icon: Icon = Inbox, title, text, action, onAction, testId = 'empty-state' }) => (
  <div data-testid={testId} className="surface flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
    <div className="grid h-12 w-12 place-items-center rounded-xl border border-white/10 bg-white/5">
      <Icon className="h-5 w-5 text-primary" />
    </div>
    <div className="font-display text-base font-semibold">{title}</div>
    {text && <p className="max-w-md text-sm text-muted-foreground">{text}</p>}
    {action && (
      <Button variant="secondary" className="mt-1 border border-white/10" onClick={onAction} data-testid={`${testId}-action`}>
        {action}
      </Button>
    )}
  </div>
);

export const SectionHeader = ({ eyebrow, title, text, right, id }) => (
  <div className="mb-6 flex flex-col gap-4 sm:mb-8 md:flex-row md:items-end md:justify-between">
    <div className="max-w-2xl">
      {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
      <h2 id={id} className="text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h2>
      {text && <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">{text}</p>}
    </div>
    {right}
  </div>
);

export const FieldError = ({ msg, id }) => (msg ? <p id={id} role="alert" className="mt-1 text-xs text-[rgb(255,120,140)]">{msg}</p> : null);
